import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Trash2, GripVertical, ExternalLink, Filter as FilterIcon, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { COUNTRIES } from "@/lib/countries";
import { flagEmoji } from "@/lib/format";
import { cn, uid } from "@/lib/utils";
import { http } from "@/lib/api";
import { LandingPreview } from "@/components/settings/tenant/FilterLandingsLibrary";

const DEVICES = ["Desktop", "Mobile", "Tablet", "Smart TV", "Console", "Other"];
const COND_TYPES = [["country", "Country"], ["bot", "Bot"], ["device", "Device type"]];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function condText(c) {
  if (c.type === "country") {
    const list = (c.values || []).join(", ") || "…";
    return `Country ${c.mode === "deny" ? "is NOT one of" : "is one of"} [${list}]`;
  }
  if (c.type === "bot") return c.value ? "Visitor IS a bot" : "Visitor is NOT a bot";
  if (c.type === "device") return `Device is one of [${(c.values || []).join(", ") || "…"}]`;
  return "";
}

function ruleSummary(rule) {
  const conds = (rule.conditions || []).filter((c) => c.type);
  if (!conds.length) return "No conditions yet — this rule will never match.";
  const joiner = (rule.match_mode || "AND") === "AND" ? " AND " : " OR ";
  const when = conds.map(condText).join(joiner);
  const act = rule.action || {};
  const then = act.type === "landing"
    ? `show landing "${act.landing?.name || (act.landing?.id ? "uploaded ZIP" : "— upload a ZIP —")}"`
    : act.type === "payout_zero"
      ? "let traffic through normally, force payout to $0"
      : `redirect to ${act.url || "— set a URL —"}`;
  return `Matches when ${when} → ${then}`;
}

function MultiCountry({ values = [], onChange, testId }) {
  const [open, setOpen] = useState(false);
  const set = new Set(values);
  const toggle = (code) => {
    const next = new Set(set);
    next.has(code) ? next.delete(code) : next.add(code);
    onChange(Array.from(next));
  };
  return (
    <div className="flex-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" data-testid={testId} role="combobox"
            className="flex min-h-10 w-full flex-wrap items-center gap-1 rounded-md border border-white/[0.1] bg-[#0A0A0A] px-2 py-1.5 text-sm text-foreground hover:border-white/20">
            {values.length === 0 && <span className="px-1 text-muted-foreground">Select countries…</span>}
            {values.map((c) => (
              <span key={c} className="flex items-center gap-1 rounded bg-white/[0.08] px-1.5 py-0.5 text-xs">
                {flagEmoji(c)} {c}
              </span>
            ))}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] border-white/[0.1] bg-[#1a1a1e] p-0" align="start">
          <Command className="bg-transparent">
            <CommandInput placeholder="Search country…" className="text-sm" />
            <CommandList>
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup>
                {COUNTRIES.map((c) => (
                  <CommandItem key={c.code} value={`${c.name} ${c.code}`}
                    data-testid={testId ? `${testId}-opt-${c.code}` : undefined}
                    onSelect={() => toggle(c.code)}>
                    <span className="mr-2 text-base leading-none">{flagEmoji(c.code)}</span>
                    <span className="flex-1">{c.name}</span>
                    <span className="mr-1 text-xs text-muted-foreground">{c.code}</span>
                    {set.has(c.code) && <span className="text-[#22C55E]">✓</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function Seg({ options, value, onChange, testId }) {
  return (
    <div className="inline-flex overflow-hidden rounded-md border border-white/[0.1]" data-testid={testId}>
      {options.map(([v, l]) => (
        <button key={v} type="button" onClick={() => onChange(v)}
          data-testid={testId ? `${testId}-${v}` : undefined}
          className={cn("px-3 py-1.5 text-xs font-medium transition-colors",
            value === v ? "bg-white text-black" : "text-muted-foreground hover:text-foreground")}>
          {l}
        </button>
      ))}
    </div>
  );
}

function ConditionRow({ cond, onChange, onRemove, idx, ruleIdx }) {
  const t = cond.type;
  const tid = `filter-${ruleIdx}-cond-${idx}`;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-white/[0.06] bg-[#0A0A0A] p-2.5" data-testid={tid}>
      <select value={t} onChange={(e) => {
        const nt = e.target.value;
        onChange(nt === "country" ? { id: cond.id, type: "country", mode: "allow", values: [] }
          : nt === "bot" ? { id: cond.id, type: "bot", value: false }
          : { id: cond.id, type: "device", values: [] });
      }} data-testid={`${tid}-type`}
        className="h-9 rounded-md border border-white/[0.1] bg-[#0A0A0A] px-2 text-sm text-foreground">
        {COND_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>

      {t === "country" && (<>
        <select value={cond.mode} onChange={(e) => onChange({ ...cond, mode: e.target.value })}
          data-testid={`${tid}-mode`} className="h-9 rounded-md border border-white/[0.1] bg-[#0A0A0A] px-2 text-sm text-foreground">
          <option value="allow">is one of ✓ (match these)</option>
          <option value="deny">is NOT one of (match everything except)</option>
        </select>
        <MultiCountry values={cond.values} onChange={(v) => onChange({ ...cond, values: v })} testId={`${tid}-countries`} />
      </>)}

      {t === "bot" && (
        <Seg options={[["true", "Is a bot"], ["false", "Is NOT a bot"]]}
          value={String(!!cond.value)} onChange={(v) => onChange({ ...cond, value: v === "true" })} testId={`${tid}-bot`} />
      )}

      {t === "device" && (
        <div className="flex flex-1 flex-wrap gap-1.5">
          {DEVICES.map((d) => {
            const on = (cond.values || []).includes(d);
            return (
              <button key={d} type="button" data-testid={`${tid}-dev-${d.replace(/\s+/g, "-")}`}
                onClick={() => onChange({ ...cond, values: on ? cond.values.filter((x) => x !== d) : [...(cond.values || []), d] })}
                className={cn("flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                  on ? "border-[#22C55E] bg-[#22C55E] text-black"
                     : "border-white/[0.14] text-muted-foreground hover:border-white/30 hover:text-foreground")}>
                {on && <Check className="h-3 w-3" />} {d}
              </button>
            );
          })}
        </div>
      )}

      <button type="button" onClick={onRemove} data-testid={`${tid}-remove`}
        className="ml-auto text-muted-foreground hover:text-[#EF4444]"><Trash2 className="h-4 w-4" /></button>
    </div>
  );
}

function ActionEditor({ rule, onChange, ruleIdx }) {
  const { data } = useQuery({ queryKey: ["filter-landings"], queryFn: async () => (await http.get("/filter-landings")).data.items });
  const landings = data || [];
  const act = rule.action || { type: "redirect", url: "" };
  const setAct = (patch) => onChange({ ...rule, action: { ...act, ...patch } });
  const tid = `filter-${ruleIdx}-action`;
  const selected = act.landing?.id ? landings.find((l) => l.id === act.landing.id) : null;

  return (
    <div className="space-y-3 rounded-lg border border-white/[0.08] bg-[#0A0A0A] p-3" data-testid={tid}>
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground">When matched, send traffic to:</span>
        <Seg options={[["redirect", "HTTP Redirect"], ["landing", "Local Landing (ZIP)"], ["payout_zero", "Payout 0"]]}
          value={act.type} onChange={(v) => setAct({ type: v })} testId={`${tid}-type`} />
      </div>

      {act.type === "payout_zero" && (
        <p className="text-xs text-muted-foreground" data-testid={`${tid}-payout-zero-hint`}>
          Traffic keeps flowing through the normal offer funnel — no redirect, no landing. Every conversion
          of a matched click records full advertiser revenue as usual, but the affiliate payout is $0.
        </p>
      )}

      {act.type === "redirect" && (
        <div className="space-y-1.5">
          <Input value={act.url || ""} onChange={(e) => setAct({ url: e.target.value })}
            placeholder="https://example.com/landing" data-testid={`${tid}-url`}
            className="border-white/[0.1] bg-[#0A0A0A]" />
          {rule.enabled !== false && !(act.url || "").trim() && (
            <p className="text-xs text-[#EF4444]" data-testid={`${tid}-url-required`}>
              Redirect URL is required — an enabled rule without a destination cannot be saved.
            </p>
          )}
        </div>
      )}

      {act.type === "landing" && (
        <div className="space-y-2">
          <select value={act.landing?.id || ""} data-testid={`${tid}-landing-select`}
            onChange={(e) => {
              const l = landings.find((x) => x.id === e.target.value);
              setAct({ landing: l ? { id: l.id, name: l.name } : null });
            }}
            className="h-10 w-full rounded-md border border-white/[0.1] bg-[#0A0A0A] px-2 text-sm text-foreground">
            <option value="">Select a landing from the library…</option>
            {landings.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
          {rule.enabled !== false && !act.landing?.id && landings.length > 0 && (
            <p className="text-xs text-[#EF4444]" data-testid={`${tid}-landing-required`}>
              Choose a landing — an enabled rule without a destination cannot be saved.
            </p>
          )}
          {landings.length === 0 && (
            <p className="text-xs text-muted-foreground">No landings in the library yet. A Tenant Admin can add them in Settings → Filter Landings.</p>
          )}
          {selected && (
            <div data-testid={`${tid}-preview`}>
              <div className="mb-1 flex items-center gap-2 text-[11px] text-muted-foreground">
                <span>Preview · {selected.file_count} files</span>
                <a href={`${BACKEND}${selected.url}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[#22C55E]" data-testid={`${tid}-open`}>Open <ExternalLink className="h-3 w-3" /></a>
              </div>
              <LandingPreview previewFileId={selected.preview_file_id} height="h-56" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RuleCard({ rule, idx, onChange, onRemove, offerId }) {
  const setConds = (conds) => onChange({ ...rule, conditions: conds });
  return (
    <div className="space-y-3 rounded-xl border border-white/[0.08] bg-[#141417] p-4" data-testid={`filter-rule-${idx}`}>
      <div className="flex items-center gap-2">
        <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground/40" />
        <Input value={rule.name || ""} onChange={(e) => onChange({ ...rule, name: e.target.value })}
          placeholder={`Rule ${idx + 1}`} data-testid={`filter-rule-${idx}-name`}
          className="h-9 flex-1 border-white/[0.1] bg-[#0A0A0A] text-sm font-medium" />
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Match</span>
          <Seg options={[["AND", "ALL (AND)"], ["OR", "ANY (OR)"]]} value={rule.match_mode || "AND"}
            onChange={(v) => onChange({ ...rule, match_mode: v })} testId={`filter-rule-${idx}-mode`} />
        </div>
        <Switch checked={rule.enabled !== false} onCheckedChange={(v) => onChange({ ...rule, enabled: v })}
          data-testid={`filter-rule-${idx}-enabled`} />
        <button type="button" onClick={onRemove} data-testid={`filter-rule-${idx}-remove`}
          className="text-muted-foreground hover:text-[#EF4444]"><Trash2 className="h-4 w-4" /></button>
      </div>

      <div className="space-y-2">
        {(rule.conditions || []).map((c, ci) => (
          <ConditionRow key={c.id} cond={c} idx={ci} ruleIdx={idx}
            onChange={(nc) => setConds(rule.conditions.map((x) => (x.id === c.id ? nc : x)))}
            onRemove={() => setConds(rule.conditions.filter((x) => x.id !== c.id))} />
        ))}
        <Button type="button" variant="ghost" size="sm" data-testid={`filter-rule-${idx}-add-cond`}
          onClick={() => setConds([...(rule.conditions || []), { id: uid(), type: "country", mode: "allow", values: [] }])}
          className="gap-2 text-muted-foreground hover:text-foreground">
          <Plus className="h-3.5 w-3.5" /> Add condition
        </Button>
      </div>

      <ActionEditor rule={rule} onChange={onChange} offerId={offerId} ruleIdx={idx} />

      <div className="rounded-lg border border-[#22C55E]/20 bg-[#22C55E]/[0.06] px-3 py-2 text-xs text-[#22C55E]" data-testid={`filter-rule-${idx}-summary`}>
        {ruleSummary(rule)}
      </div>
    </div>
  );
}

export function OfferFilters({ value = [], onChange, offerId }) {
  const rules = value || [];
  const addRule = () => onChange([...rules, {
    id: uid(), name: "", enabled: true, match_mode: "AND",
    conditions: [{ id: uid(), type: "country", mode: "allow", values: [] }],
    action: { type: "redirect", url: "" },
  }]);
  return (
    <div className="space-y-4" data-testid="offer-filters">
      <div className="flex items-start gap-2 rounded-xl border border-white/[0.06] bg-[#0A0A0A] p-4 text-xs text-muted-foreground">
        <FilterIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#22C55E]" />
        <p>Incoming traffic is checked against these rules <b className="text-foreground">top to bottom</b> — the
          <b className="text-foreground"> first matching rule wins</b> and its action runs instead of sending the visitor to the offer.
          Within a rule, <b className="text-foreground">ALL (AND)</b> requires every condition; <b className="text-foreground">ANY (OR)</b> needs just one.
          The click is still recorded in the offer's stats. No rules = offer works as usual.</p>
      </div>

      {rules.map((r, i) => (
        <RuleCard key={r.id} rule={r} idx={i} offerId={offerId}
          onChange={(nr) => onChange(rules.map((x) => (x.id === r.id ? nr : x)))}
          onRemove={() => onChange(rules.filter((x) => x.id !== r.id))} />
      ))}
      {rules.length === 0 && (
        <div className="rounded-xl border border-dashed border-white/[0.1] py-12 text-center text-sm text-muted-foreground">
          No filter rules yet — traffic goes straight to the offer. Add a rule to redirect, serve a local landing or zero the payout for specific traffic.
        </div>
      )}

      <div className="flex justify-center pt-1">
        <Button type="button" variant="outline" onClick={addRule} data-testid="filter-add-rule"
          className="gap-2 border-white/[0.12] px-6"><Plus className="h-4 w-4" /> Add filter rule</Button>
      </div>
    </div>
  );
}
